import { useTranslation } from 'react-i18next';
import { CalendarCheck, Phone } from 'lucide-react';
import { isOutsideWindow, windowLabel } from 'constants/timeWindows';
import type { InterventionRequest } from 'api/interventionRequestsApi';
import { formatDay } from 'utils/formatDate';
import { parisDateTime } from 'utils/interventionDate';
import { technicianName } from './upcomingAssignment';

interface ChosenSlotBlockProps {
  /** The accepted request of the mission, or null when nobody accepted yet. */
  acceptor: InterventionRequest | null;
}

/** « Passage choisi » (E4, E12): the technician's day and time, the window asked, the store phone. */
const ChosenSlotBlock = ({ acceptor }: ChosenSlotBlockProps) => {
  const { t } = useTranslation();

  if (!acceptor) {
    return (
      <p className="flex items-center gap-2 rounded-md border border-dashed px-3 py-2.5 text-[13px] text-muted-foreground">
        <CalendarCheck className="h-4 w-4 shrink-0" aria-hidden="true" />
        {t('Chosen slot — nobody has accepted yet')}
      </p>
    );
  }

  const requested = windowLabel(t, acceptor.timeWindow);
  const chosenOn = acceptor.respondedAt ? parisDateTime(acceptor.respondedAt) : null;
  const outside = isOutsideWindow(acceptor.timeWindow, acceptor.chosenTime);
  const { storePhone } = acceptor;

  return (
    <section className="flex flex-col gap-1 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2.5 dark:border-emerald-900 dark:bg-emerald-950">
      <p className="flex flex-wrap items-center gap-2 text-[13.5px] font-semibold text-emerald-900 dark:text-emerald-100">
        <CalendarCheck className="h-4 w-4 shrink-0" aria-hidden="true" />
        {/* A pre-L6 acceptance carries no slot: said, never « — à — ». */}
        {acceptor.chosenDate
          ? t('Chosen slot by {{name}} — {{day}} at {{time}}', {
              name: technicianName(acceptor),
              day: formatDay(acceptor.chosenDate),
              time: acceptor.chosenTime ?? '—',
            })
          : t('{{name}} accepted — no slot chosen', { name: technicianName(acceptor) })}
        {outside && (
          <span className="rounded-md bg-amber-100 px-1.5 py-0.5 text-[11.5px] font-medium text-amber-800 dark:bg-amber-900 dark:text-amber-200">
            {t('outside the window')}
          </span>
        )}
      </p>
      {requested && (
        <p className="text-[12.5px] text-muted-foreground">
          {t('Requested window: {{window}} · chosen on {{date}} at {{time}}', {
            window: requested,
            date: chosenOn?.date ?? '—',
            time: chosenOn?.time ?? '—',
          })}
        </p>
      )}
      <p className="flex flex-wrap items-center gap-1.5 text-[12.5px]">
        {storePhone && (
          <>
            <Phone className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
            <span className="text-muted-foreground">{t('Store phone')}</span>
            <a
              href={`tel:${storePhone.replace(/\s+/g, '')}`}
              className="font-medium underline underline-offset-2"
            >
              {storePhone}
            </a>
            <span className="text-muted-foreground" aria-hidden="true">·</span>
          </>
        )}
        <span className="text-muted-foreground">{t('Notify the store.')}</span>
      </p>
    </section>
  );
};

export default ChosenSlotBlock;
